<template>
  <div>
    <div class="config-image__container config-image__container--alt-gradimages">
      <div
        v-if="isFavorite"
        class="config-image__favorite-icon-wrapper"
      >
        <i class="v2-icon-favorite config-image__favorite-icon"/>
      </div>
      <div class="config-image__inner-container">
        <img
          :src="selectedFrame.Urls.SmallWatermarked"
          :class="selectedImgClasses"
          alt="Selected image"
        >
      </div>
      <div class="config-image__button-container">
        <div class="config-image__top-buttons">
          <div
            v-if="allowRetouching"
            :class="retouchingButtonClasses"
          >
            <div
              :aria-label="contentfulText('enhancements-label').Content"
              class="config-image__button-icon config-image__button-icon--enhancements"
              @click="handleRetouchingsClick"
            />
          </div>
          <div
            v-if="allowCropping"
            :class="cropButtonClasses"
          >
            <div
              :aria-label="contentfulText('crop-label').Content"
              class="config-image__button-icon config-image__button-icon--crop"
              @click="handleCropClick"
            />
          </div>
        </div>
        <div class="config-image__bottom-buttons">
          <div
            class="config-image__button config-image__button--change"
            @click="handleChangePhotoClick"
          >
            <contentful-text :item="contentfulText('change-photo-label')" />
          </div>
        </div>
      </div>
      <div class="config-image__modifications">
        <div
          v-if="numberRetouchingsApplied > 0"
          :title="enhancementsModificationTitle"
          class="config-image__modification config-image__modification--not-hidden config-image__modification--retouching"
        >
          {{ numberRetouchingsApplied }}
        </div>
        <div
          v-if="selectedFrame.IsCropped"
          :title="contentfulText('custom-crop-label').Content"
          class="config-image__modification config-image__modification--not-hidden config-image__modification--crop"
        >
          <contentful-text :item="contentfulText('custom-crop-label')" />
        </div>
      </div>
      <div
        v-if="allowQuantityUpdate"
        class="config-image__quantity-container"
      >
        <div class="row">
          <div class="col-xs-4">
            <div
              class="config-image__quantity-minus"
            >
              <div
                v-if="quantity === 1"
                class="config-image__remove config-image__remove--visible"
                @click="showRemoveConfirmModal = true"
              />
              <div
                v-if="quantity > 1"
                class="config-image__quantity-minus-sign"
                @click="handleQtyDownClick"
              >-</div>
            </div>
          </div>
          <div class="col-xs-4">
            <input
              v-model="quantityModel"
              class="config-image__quantity-input"
              @blur="handleQuantityInputBlur"
            >
          </div>
          <div class="col-xs-4">
            <div
              :class="qtyPlusClasses"
              @click="handleQtyUpClick"
            >+</div>
          </div>
        </div>
      </div>
    </div>
    <crop-modal
      :show="showCropModal"
      :frame="selectedFrame"
      :aspect-ratio-name="optionProduct.AspectRatioName"
      :aspect-ratio="optionProduct.AspectRatio"
      :saved-crop="savedCropData"
      @close="showCropModal = false"
    />
    <remove-selection-confirm-modal
      :show="showRemoveConfirmModal"
      @close="showRemoveConfirmModal = false"
      @confirm="handleRemoveConfirm"
    />
  </div>
</template>

<script>
import ContentfulMixin from '@Shared/mixins/contentful'
import CropModal from '@Shared/components/catalog/CropModal/CropModal'
import RemoveSelectionConfirmModal from './RemoveSelectionConfirmModal'
import { find } from 'lodash'

export default {
  name: 'PhotoSelectionSlotSelectedFrame',
  components: {
    CropModal,
    RemoveSelectionConfirmModal
  },
  mixins: [ContentfulMixin],
  props: {
    selectedFrameId: { type: Number, default: null, required: true },
    quantity: { type: Number, default: null, required: true },
    optionProduct: { type: Object, default: null, required: true },
    allowQuantityUpdate: { type: Boolean, default: true, required: false }
  },
  data () {
    return {
      showCropModal: false,
      showRemoveConfirmModal: false,
      savedCropData: null,
      quantityModel: null
    }
  },
  computed: {
    selectedFrame () {
      return this.$store.getters.getFrame(this.selectedFrameId)
    },
    isFavorite () {
      return this.selectedFrame.IsFavorite
    },
    allowRetouching () {
      return this.$store.state.catalogPageCommon.allowRetouching
    },
    allowCropping () {
      return this.$store.state.catalogPageCommon.allowCropping
    },
    numberRetouchingsApplied () {
      return this.selectedFrame.RetouchingPackages.length + this.$store.getters.selectedRetouchingsByFrame(this.selectedFrame.FrameID).length
    },
    enhancementsModificationTitle () {
      return this.contentfulTextFormatted('enhancements-count', { numEnhancements: this.numberRetouchingsApplied })
    },
    remainingEmptyFramesCount () {
      return this.$store.getters.getRemainingEmptyFramesByProduct(this.optionProduct.CatalogPackageOptionProductID)
    },
    selectedImgClasses () {
      return {
        'responsive-img': true,
        'selected-image--landscape': this.selectedFrame.Orientation === 0
      }
    },
    retouchingButtonClasses () {
      return {
        'config-image__button': true,
        'config-image__button--full-width': this.allowRetouching && !this.allowCropping
      }
    },
    cropButtonClasses () {
      return {
        'config-image__button': true,
        'config-image__button--full-width': this.allowCropping && !this.allowRetouching
      }
    },
    qtyPlusClasses () {
      return {
        'config-image__quantity-plus': true,
        'config-image__quantity-plus--disabled': this.remainingEmptyFramesCount === 0
      }
    },
    content () {
      return this.$store.state.mixMatchPhotoSelectionModuleContent
    }
  },
  watch: {
    quantity (v, ov) {
      this.quantityModel = v
    },
    quantityModel (v, ov) {
      if (this.allowQuantityUpdate === false) {
        return
      }

      if (v.length === 0) {
        return
      }

      if (isNaN(v)) {
        this.quantityModel = this.quantity
      } else if (v < 1) {
        this.quantityModel = this.quantity
      }

      const maxQtyAllowed = this.remainingEmptyFramesCount + this.quantity

      if (v > maxQtyAllowed) {
        this.quantityModel = maxQtyAllowed
      }

      this.$emit('quantity-updated', parseInt(this.quantityModel, 10))
    }
  },
  mounted () {
    this.quantityModel = this.quantity
  },
  methods: {
    handleChangePhotoClick () {
      this.$emit('change-photo-clicked')
    },
    handleRetouchingsClick () {
      document.dispatchEvent(new CustomEvent('retouchingModal.show', {
        detail: { frameId: this.selectedFrame.FrameID, alwaysShow: true }
      }))
    },
    handleCropClick () {
      if (this.selectedFrame.IsCropped === true) {
        // get saved crop data
        this.$http.post(`${this.$store.state.catalogPageCommon.siteRoot}Ajax/GetFrameCropData`, { frameId: this.selectedFrame.FrameID }).then(resp => {
          if (resp.status !== 200) {
            this.savedCropData = null
            return
          }

          // get the crop data for the option product's aspect ratio
          let cropMatchingAspectRatio = find(resp.data.CroppingData[0].Value.Crops, crop => {
            return crop.AspectRatioTypeString === this.optionProduct.AspectRatioName && crop.TargetHeight > 0 && crop.TargetWidth > 0
          })

          if (cropMatchingAspectRatio !== undefined && cropMatchingAspectRatio !== false) {
            this.savedCropData = cropMatchingAspectRatio
          } else {
            this.savedCropData = null
          }
        }).catch(e => {
          this.savedCropData = null
        }).finally(() => {
          this.showCropModal = true // show the crop modal
        })
      } else {
        this.showCropModal = true
      }
    },
    handleRemoveConfirm () {
      this.$emit('remove-clicked')
    },
    handleQtyDownClick () {
      this.$emit('quantity-updated', (this.quantity - 1))
    },
    handleQtyUpClick () {
      if (this.remainingEmptyFramesCount === 0) {
        return
      }

      this.$emit('quantity-updated', (this.quantity + 1))
    },
    handleQuantityInputBlur () {
      if (this.quantityModel.length === 0) {
        this.quantityModel = this.quantity
      }
    }
  }
}
</script>

<style>
.selected-image--landscape {
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/default/catalog-mix/components/PhotoSelection/Slot/SelectedFrame.vue